import { useCallback, useEffect, useState } from 'react'
import type { ReactElement } from 'react'
import Dialog from '../../../Common/Dialog/Dialog'
import DialogContent from '../../../Common/Dialog/DialogContent'
import DialogTitle from '../../../Common/Dialog/DialogTitle'
import { useTranslation } from 'react-i18next'
import ItemList from '../../../Common/ItemList/ItemList'
import ProductDesign from '../../Products/ProductDesign'
import ProductSearchBar, { type ProductSearchParams } from '../../Products/ProductSearchBar'
import { useAppSelector } from '../../../Common/_hooks/useAppSelector'
import { useSearchParams } from 'react-router'
import { Box, Button, Grid } from '@mui/material'
import type { UserProductDesignStore } from '../../../UserData/_stores/UserProductDesignStore'
import { KeyboardBackspaceRounded as KeyboardBackspaceRoundedIcon } from '@mui/icons-material'
import type { ProductItem } from '../../../UserData/_stores/UserCartStore'
import LayoutSelect from '../../../Common/ItemList/LayoutSelect'
import ProductSkeletonList from '../../Products/ProductSkeletonList'
import AccountNoResults from '../../AccountNoResults'
import OrderDialogContent from '../../Products/OrderDialog/OrderDialogContent'

interface Props {
  productDesignId?: number
  item?: ProductItem
  onConfirm: (productDesign: UserProductDesignStore, listOptions: Immutable.Map<number, number>, listBrandingOptions: Immutable.Map<number, number>) => void
  opened: boolean
  onClose: () => void
}

const SEARCH_FIELDS = ['search', 'categories', 'collections']

export default function SyncProductDialog(props: Props) {
  const [t] = useTranslation('account-v2')
  const [searchParams] = useSearchParams()

  const userLayout = useAppSelector(state => state.get('userData').get('prefs').get('accountProductsLayout'))
  const userLimit = useAppSelector(state => state.get('userData').get('prefs').get('accountPageLimit'))

  const [selectedProductDesignId, setSelectedProductDesignId] = useState<number | undefined>(props.productDesignId)
  const selectedProductDesign = useAppSelector(state => selectedProductDesignId !== undefined ? state.get('userData').get('productDesigns').get('data').get(String(selectedProductDesignId)) : undefined)

  const [layout, setLayout] = useState(userLayout)
  const categories = searchParams.get('categories')
  const searchQuery = searchParams.get('search')
  const getInitialSearch = useCallback((): ProductSearchParams => ({
    categories: categories ? categories.split(',') : [],
    search: searchQuery ? searchQuery.split(',') : [],
    collections: []
  }), [categories, searchQuery])
  const [search, setSearch] = useState<ProductSearchParams>(getInitialSearch)

  const resetDialogState = useCallback(() => {
    setSelectedProductDesignId(props.productDesignId)
    setLayout(userLayout)
    setSearch(getInitialSearch())
  }, [getInitialSearch, props.productDesignId, userLayout])

  const onClose = useCallback(() => {
    props.onClose()
  }, [props.onClose])

  useEffect(() => {
    setSelectedProductDesignId(props.productDesignId)
  }, [props.productDesignId])

  const renderItem = useCallback((item: UserProductDesignStore) => {
    return <ProductDesign
      item={item}
      view={layout}
      actions={<Button
        variant="contained"
        color="accent"
        onClick={() => {
          setSelectedProductDesignId(item.get('id'))
        }}
      >{t('Select')}</Button>}
    />
  }, [t, layout])

  const renderList = useCallback((items: ReactElement | null, links: ReactElement | null, loading: boolean) => {
    return <>
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {loading
          ? <ProductSkeletonList
            count={userLimit}
            layout={layout}
            showSelection={false}
            showSecondaryActions={false}
          />
          : (items ?? <Grid item xs={12}>
            <AccountNoResults compact />
          </Grid>
          )
        }
      </Grid>

      {links}
    </>
  }, [t, userLimit, layout])

  const onClearSelectedProduct = useCallback(() => setSelectedProductDesignId(undefined), [])

  const onOrderDialogConfirm = useCallback((listOptions: Immutable.Map<number, number>, listBrandingOptions: Immutable.Map<number, number>) => {
    if (selectedProductDesign === undefined) return;

    props.onConfirm(selectedProductDesign, listOptions, listBrandingOptions)
  }, [selectedProductDesign, props.onConfirm])

  return <Dialog
    open={props.opened}
    maxWidth="lg"
    fullWidth={selectedProductDesignId === undefined}
    hasTabs={selectedProductDesignId !== undefined}
    onClose={onClose}
    TransitionProps={{
      onExited: resetDialogState,
    }}
    showCloseButton
  >
    {selectedProductDesignId === undefined ? <DialogTitle>{t('Sync product')}</DialogTitle> : null}
    {selectedProductDesignId === undefined ? <DialogContent>
      <ProductSearchBar
        placeholder={t('Search your products...')}
        value={search}
        onChange={setSearch}
      >
        <LayoutSelect
          value={layout}
          onChange={setLayout}
        />
      </ProductSearchBar>
      <ItemList
        dataType={'productDesigns'}
        controlled={true}
        item={renderItem}
        searchQuery={search}
        searchFields={SEARCH_FIELDS}
        limit={userLimit}
        render={renderList}
      />
    </DialogContent> : <>
      <Box component="div" sx={{
        padding: '10px 25px 0',
        pl: {
          xs: 0.25,
          sm: 1.25
        },
        pt: {
          xs: 0.5,
          sm: 1,
        },
        mb: {
          xs: -1,
          sm: -2,
        },
        position: 'relative',
        zIndex: 1,
      }}>
        <Button
          variant="text"
          color="secondary"
          startIcon={<KeyboardBackspaceRoundedIcon />}
          onClick={onClearSelectedProduct}
        >{t('Back to products')}</Button>
      </Box>
      <OrderDialogContent
        opened={true}
        productDesignId={selectedProductDesignId}
        item={props.item}
        onConfirm={onOrderDialogConfirm}
        onClose={onClearSelectedProduct}
        showTabs={false}
      />
    </>}
  </Dialog>
}
